import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  fetchSubscriptionStatus,
  fetchWordAlternatives,
  fetchUsage,
  fetchPricing,
  fetchFavorites,
  fetchHistory,
  deleteFavorite as deleteFavoriteRequest,
  deleteHistoryEntry as deleteHistoryEntryRequest,
  saveFavorite as saveFavoriteRequest,
  saveHistoryEntry as saveHistoryEntryRequest,
  sendMagicLink as sendMagicLinkRequest,
  verifyMagicLink as verifyMagicLinkRequest,
  verifyLoginCode as verifyLoginCodeRequest,
  createCheckoutSession as createCheckoutSessionRequest,
  openCustomerPortal as openCustomerPortalRequest,
  restoreSubscriptionStatus as restoreSubscriptionStatusRequest,
  fetchAuthSession,
  requestEmailChange as requestEmailChangeRequest,
  createAccountLinkCode as createAccountLinkCodeRequest,
  fetchAccountLinkStatus,
  unlinkAccount as unlinkAccountRequest,
  type AccountLinkCode,
  type AuthSession,
  type EmailChangeResult,
  type SubscriptionStatus,
  type WordAlternativesOptions,
  type WordAlternativesResult,
  type UsageSnapshot,
  type PricingInfo,
  type FavoriteEntry,
  type HistoryEntry,
  type SubscriptionRestoreResult,
} from '@/lib/baba-api'

export type SendMagicLinkArg = { email: string; returnUrl?: string; referralCode?: string | null }
export type VerifyMagicLinkArg = { email: string; token: string }
export type CreateCheckoutArg = {
  plan: 'monthly' | 'yearly'
  tier?: 'pro' | 'pro_plus'
  successUrl?: string
  cancelUrl?: string
  promoCode?: string
  trialDays?: number
  locale?: string
}
export type CreateCheckoutResult = {
  url?: string
  sessionId?: string
  error?: string
  alreadySubscribed?: boolean
  manageUrl?: string
  authRequired?: boolean
  provider?: 'paddle'
  transactionId?: string
  email?: string | null
}

export const babaApi = createApi({
  reducerPath: 'babaApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Subscription', 'Usage', 'Pricing', 'Library'],
  endpoints: (builder) => ({
    subscriptionStatus: builder.query<SubscriptionStatus, void>({
      async queryFn() {
        try {
          const data = await fetchSubscriptionStatus()
          return { data }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
      providesTags: [{ type: 'Subscription', id: 'status' }],
    }),

    usage: builder.query<UsageSnapshot | null, void>({
      async queryFn() {
        try {
          const data = await fetchUsage()
          return { data }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
      providesTags: ['Usage'],
    }),

    pricing: builder.query<PricingInfo | null, void>({
      async queryFn() {
        try {
          const data = await fetchPricing()
          return { data }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
      providesTags: ['Pricing'],
    }),

    libraryFavorites: builder.query<FavoriteEntry[], void>({
      async queryFn() {
        try {
          const data = await fetchFavorites()
          return { data }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
      providesTags: [{ type: 'Library', id: 'favorites' }],
    }),

    libraryHistory: builder.query<HistoryEntry[], void>({
      async queryFn() {
        try {
          const data = await fetchHistory()
          return { data }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
      providesTags: [{ type: 'Library', id: 'history' }],
    }),

    wordAlternatives: builder.mutation<WordAlternativesResult, WordAlternativesOptions>({
      async queryFn(arg) {
        const data = await fetchWordAlternatives(arg)
        return { data }
      },
    }),

    saveFavorite: builder.mutation<void, Partial<FavoriteEntry>>({
      async queryFn(entry) {
        await saveFavoriteRequest(entry)
        return { data: undefined }
      },
      invalidatesTags: [{ type: 'Library', id: 'favorites' }],
    }),

    saveHistoryEntry: builder.mutation<void, Partial<HistoryEntry>>({
      async queryFn(entry) {
        await saveHistoryEntryRequest(entry)
        return { data: undefined }
      },
      invalidatesTags: [{ type: 'Library', id: 'history' }],
    }),

    removeFavorite: builder.mutation<void, string>({
      async queryFn(id) {
        await deleteFavoriteRequest(id)
        return { data: undefined }
      },
      invalidatesTags: [{ type: 'Library', id: 'favorites' }],
    }),

    removeHistoryEntry: builder.mutation<void, string>({
      async queryFn(id) {
        await deleteHistoryEntryRequest(id)
        return { data: undefined }
      },
      invalidatesTags: [{ type: 'Library', id: 'history' }],
    }),

    sendMagicLink: builder.mutation<{ ok: boolean; error?: string }, SendMagicLinkArg>({
      async queryFn(params) {
        const data = await sendMagicLinkRequest(params)
        return { data }
      },
    }),

    verifyMagicLink: builder.mutation<
      { token?: string; subscription_status?: string; plan?: string | null; is_new_user?: boolean; eventId?: string; error?: string },
      VerifyMagicLinkArg
    >({
      async queryFn(params) {
        const data = await verifyMagicLinkRequest(params)
        return { data }
      },
    }),

    verifyLoginCode: builder.mutation<
      { token?: string; subscription_status?: string; plan?: string | null; is_new_user?: boolean; eventId?: string; error?: string; remaining?: number },
      { email: string; code: string }
    >({
      async queryFn(params) {
        const data = await verifyLoginCodeRequest(params)
        return { data }
      },
    }),

    createCheckoutSession: builder.mutation<CreateCheckoutResult, CreateCheckoutArg>({
      async queryFn(params) {
        const data = await createCheckoutSessionRequest(params)
        return { data }
      },
    }),

    customerPortal: builder.mutation<{ url?: string; error?: string }, string | undefined>({
      async queryFn(returnUrl) {
        const data = await openCustomerPortalRequest(returnUrl)
        return { data }
      },
    }),

    restoreSubscription: builder.mutation<SubscriptionRestoreResult, void>({
      async queryFn() {
        const data = await restoreSubscriptionStatusRequest()
        return { data }
      },
      invalidatesTags: [{ type: 'Subscription', id: 'status' }, 'Usage'],
    }),

    authSession: builder.query<AuthSession | null, void>({
      async queryFn() {
        try {
          const data = await fetchAuthSession()
          return { data }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
      providesTags: [{ type: 'Subscription', id: 'session' }],
    }),

    requestEmailChange: builder.mutation<EmailChangeResult, string>({
      async queryFn(newEmail) {
        const data = await requestEmailChangeRequest(newEmail)
        return { data }
      },
      invalidatesTags: [{ type: 'Subscription', id: 'session' }],
    }),

    accountLinkStatus: builder.query<{ linked: boolean }, void>({
      async queryFn() {
        try {
          return { data: await fetchAccountLinkStatus() }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
    }),

    createAccountLinkCode: builder.mutation<AccountLinkCode, void>({
      async queryFn() {
        try {
          return { data: await createAccountLinkCodeRequest() }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
    }),

    unlinkAccount: builder.mutation<{ unlinked: boolean }, void>({
      async queryFn() {
        try {
          return { data: await unlinkAccountRequest() }
        } catch (e) {
          return { error: { status: 'FETCH_ERROR' as const, error: String(e) } }
        }
      },
    }),
  }),
})

export const {
  useSubscriptionStatusQuery,
  useUsageQuery,
  usePricingQuery,
  useLibraryFavoritesQuery,
  useLibraryHistoryQuery,
  useWordAlternativesMutation,
  useSaveFavoriteMutation,
  useSaveHistoryEntryMutation,
  useRemoveFavoriteMutation,
  useRemoveHistoryEntryMutation,
  useSendMagicLinkMutation,
  useVerifyMagicLinkMutation,
  useVerifyLoginCodeMutation,
  useCreateCheckoutSessionMutation,
  useCustomerPortalMutation,
  useRestoreSubscriptionMutation,
  useAuthSessionQuery,
  useRequestEmailChangeMutation,
  useAccountLinkStatusQuery,
  useCreateAccountLinkCodeMutation,
  useUnlinkAccountMutation,
} = babaApi
